import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import {getQueryParams, getTarget, hrefSSO, onClickSSO} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {
	departureStart,
	getPrice,
	getTrafficIconUrl,
	isGoodsTpFH,
	reserveButtonText,
	returnArrival,
} from "@COMPONENTS/common/eventUtil";
import {getExpectMileage} from "@COMPONENTS/common/goodsUtil";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import CouponPopup, {CouponPopType} from "@COMPONENTS/product/common/popup/desktop/CouponPopup";
import EventListPopup from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import PointSavePop from "@COMPONENTS/product/common/popup/desktop/PointSavePop";
import * as style from "@COMPONENTS/style/detailpackage/snb";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import CountingButton from "@FRAMEWORK/ui/desktop/button/CountingButton";
import AirlineIcon from "@FRAMEWORK/ui/desktop/icon/AirlineIcon";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import errorImg from "@PUBLIC/P/images/ico/ico_nodata.png";
import useCustomerMe from "@QUERIES/account/useCustomerMe";
import useBannerContents from "@QUERIES/essential/banner/useBannerContents";
import {eventAvailabilityAsync} from "@QUERIES/prdt/event/useEventAvailability";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import useEventMileage from "@QUERIES/prdt/event/useEventMileage";
import useEventPrice from "@QUERIES/prdt/event/useEventPrice";
import {GoodsAvailEvent, goodsAvailEventAsync} from "@QUERIES/prdt/goods/useGoodsOneAvailEvent";
import {format} from "@yb/utils";
import {useRouter} from "next/router";
import {memo, useCallback, useMemo, useState} from "react";

interface Props {
	eventDetail: EventDetail;
}

const DetailSNBComponent = (props: Props) => {
	const {eventDetail: event} = props;
	const {goodsCd, evCd} = event;
	const {data: loginInfo} = useCustomerMe();

	const eventPriceFetch = useEventPrice(evCd);
	const price = getPrice(eventPriceFetch.data?.body);

	const eventMileageFetch = useEventMileage(evCd);
	const mileage = eventMileageFetch.data?.body;

	const bannerContentsFetch = useBannerContents("GORV"); // WEB_CONT_DIVI_CD 상품상세_요금영역
	const bannerContents = bannerContentsFetch.data?.body;
	const router = useRouter();
	const [open] = useOverlay();
	const [slide] = useOverlay("slideIn");

	const maxCount = 30;
	// 패밀리 상품(familyYn === "Y")은 성인 2, 아동 2, 유아 0으로 기본 세팅
	const [count, setCount] = useState(event.familyYn === "Y" ? {A: 2, C: 2, I: 0} : {A: 1, C: 0, I: 0});
	const [coupon, setCoupon] = useState<CouponPopType[]>([]);

	// 쿠폰 할인금액
	const discount = useMemo(() => coupon.reduce((acc, cur) => acc + cur.calcObj.totalPrice, 0), [coupon]);

	// 총액
	const totalPrice = useMemo(() => {
		if (!price) return 0;
		return price.adult * count.A + price.child * count.C + price.infant * count.I - discount;
	}, [count, price, discount]);

	// + - 버튼
	const calculation = useCallback(
		(type: string) => (number: number) => {
			if (number > maxCount) return;
			if (discount > 0) {
				open(prop => <Alert {...prop} text="쿠폰을 재적용 해주세요." />);
				setCoupon([]);
				return;
			}
			setCount(cur => ({...cur, [type]: number}));
		},
		[discount],
	);

	/** 쿠폰 open */
	const openPopCoupon = () => {
		const callbackCoupon = (prop: CouponPopType[]) => {
			setCoupon(prop);
		};
		if (loginInfo?.body.nonUser === false) {
			open(prop => <CouponPopup {...prop} evCd={evCd} count={count} callback={callbackCoupon} />);
		} else {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					afterConfirm={() => LoginUtil.login()}
				/>
			));
		}
	};

	/** 포인트 적립 안내 open */
	const openPopPointSave = () => {
		open(prop => <PointSavePop {...prop} evCd={evCd} />);
	};

	/** 출발일보기 slide */
	const openPopEventList = async () => {
		const result = await goodsAvailEventAsync(goodsCd);
		const {body}: {body: GoodsAvailEvent} = result.data;
		// 초기 날짜 값 eventDetail.evStartDt 세팅
		const availEvent = {...body, evStartDt: event.evStartDt};
		slide(prop => <EventListPopup {...prop} goodsCd={goodsCd} availEvent={availEvent} />);
	};

	/** 예약하기 버튼 텍스트 */
	const reserveText = reserveButtonText(event);

	/** 예약하기 버튼 */
	const handleReserve = async () => {
		// 패밀리 상품은 (성인2 + 아동2)의 배수여야 예약 가능 (유아는 조건에 영향 없음)
		if (event.familyYn === "Y") {
			const isValidFamily = count.A === count.C && count.A > 0 && count.A % 2 === 0;
			if (!isValidFamily) {
				MessageUtil.alert(
					"패밀리팩 상품입니다.<br/>성인과 아동 인원을 동일하게 선택해 주세요.<br/>(예: 성인 2명 + 아동 2명)",
				);
				return;
			}
		}
		let msg = "";
		if (reserveText[1] === "예약마감") {
			msg = "예약이 마감되었습니다.<br/>출발일 변경에서 예약가능 상품으로 예약하시거나,<br/>담당자에게 문의 해주세요.";
		} else if (reserveText[1] === "출발완료") {
			msg = "이미 출발한 상품입니다.<br/>출발일 변경에서 예약가능 상품으로 예약하시거나,<br/>담당자에게 문의 해주세요.";
		}
		if (event.fixRervCd === "YT") {
			const eventAvailability = await eventAvailabilityAsync(evCd);
			if (eventAvailability.data?.body < count.A + count.C + count.I) {
				msg = "예약 가능 인원을 초과하였습니다.<br/>담당자에게 문의해 주세요.";
			}
		}
		if (msg) {
			MessageUtil.alert(msg);
			return;
		}
		const adtCnt = count.A > 1 ? `${count.A}` : "1";
		const chdCnt = count.C > 0 ? `${count.C}` : "0";
		const infCnt = count.I > 0 ? `${count.I}` : "0";
		const menu = getQueryParams(location.search)?.menu ?? "";
		const dspSid = router.query?.dspSid;
		const couponParams = coupon.map(v => v.couponNo).join(",");
		const nextUrl = `${
			publicRuntimeConfig.ENV_TRAVEL_URL
		}/pkg/reserve?evCd=${evCd}&adtCnt=${adtCnt}&chdCnt=${chdCnt}&infCnt=${infCnt}&menu=${menu}&dspSid=${dspSid}${
			couponParams ? `&coupon=${couponParams}` : ""
		}`;

		/* 로그인 안되어있으면 로그인페이지 이동 */
		if (!(loginInfo?.body.nonUser === false)) {
			LoginUtil.loginRsvn(nextUrl);
			return;
		}

		hrefSSO(nextUrl);
	};

	// tagging 기획전 배너
	const {taggingClick: taggingClickBanner} = useTaggingClick("pkg_select_promo_dtl");
	// tagging 출발일 보기
	const {taggingClick: taggingClickChange} = useTaggingClick("pkg_select_dtl_change", event);
	// tagging 예약하기
	const {taggingClick: taggingClickReserve} = useTaggingClick("pkg_select_evCd_pkgDtl", {
		eventDetail: event,
		count: {adultCnt: count.A, childCnt: count.C, infantCnt: count.I},
		coupon,
	});

	return (
		<style.Box isFixed={true}>
			<style.ScrollWrap isOption={event.accomNm && event.webDisplayCls === "B"}>
				<style.DepartureWrap>
					<style.InfoList>
						<li>
							<span>출발</span>
							<em>{departureStart(event)}</em>
						</li>
						<li>
							<span>도착</span>
							<em>{returnArrival(event)}</em>
						</li>
						<li>
							<span>교통</span>
							{event.trTp ? (
								<Flex gap="10" align="center">
									<AirlineIcon img={getTrafficIconUrl(event)} title={event.trCompanyNm} />
								</Flex>
							) : (
								<em>교통불포함</em>
							)}
						</li>
						{event.accomNm && event.webDisplayCls === "B" && (
							<li>
								<span>옵션</span>
								<em>{event.accomNm}</em>
							</li>
						)}
						{/* 허니문, 에어텔이 아닌경우 */}
						{!isGoodsTpFH(event.goodsTp) && (
							<li>
								<span>남은좌석</span>
								<em>{event.evStaCd === "05" ? `마감` : `${event.avlilBlockQty}석`}</em>
							</li>
						)}
					</style.InfoList>
					<style.DepartureButton
						types="subCtn"
						text="출발일 변경"
						size="large"
						onClick={() => {
							openPopEventList();
							taggingClickChange();
						}}
					/>
				</style.DepartureWrap>
				<style.PriceWrap>
					<li>
						<style.Assort>
							<style.Age>성인</style.Age>
							<style.PriceText>
								{format.koKRFormatNumber(price?.adult)}
								<span>원</span>
							</style.PriceText>
						</style.Assort>
						<CountingButton value={count.A} minValue={1} increase={calculation("A")} decrease={calculation("A")} />
					</li>
					<li>
						<style.Assort>
							<style.Age>아동</style.Age>
							{isGoodsTpFH(event.goodsTp) && price?.child === 0 ? (
								<>별도문의</>
							) : (
								<style.PriceText>
									{format.koKRFormatNumber(price?.child)}
									<span>원</span>
								</style.PriceText>
							)}
						</style.Assort>
						<CountingButton value={count.C} increase={calculation("C")} decrease={calculation("C")} />
					</li>

					<li>
						<style.Assort>
							<style.Age>유아</style.Age>
							{isGoodsTpFH(event.goodsTp) && price?.infant === 0 ? (
								<>별도문의</>
							) : (
								<style.PriceText>
									{format.koKRFormatNumber(price?.infant)}
									<span>원</span>
								</style.PriceText>
							)}
						</style.Assort>
						<CountingButton value={count.I} increase={calculation("I")} decrease={calculation("I")} />
					</li>
				</style.PriceWrap>
			</style.ScrollWrap>
			<style.CouponWrap>
				<Flex justify="space-between">
					<style.Coupon>할인쿠폰</style.Coupon>
					<button type="button" onClick={openPopCoupon}>
						쿠폰 적용
					</button>
				</Flex>
				<Flex justify="space-between">
					<span>쿠폰할인</span>
					<style.Discount>
						-{format.koKRFormatNumber(discount)}
						<span>원</span>
					</style.Discount>
				</Flex>
			</style.CouponWrap>
			<style.TotalWrap>
				<style.PriceInfo>
					<button type="button" onClick={openPopPointSave}>
						포인트 적립 안내
					</button>
					<p>(성인1인기준) {mileage && format.koKRFormatNumber(getExpectMileage(mileage))}P 적립예정</p>
				</style.PriceInfo>
				<style.TotalPrice $disabled={reserveText[0] === "disabled"}>
					<span>
						<strong>{format.koKRFormatNumber(totalPrice)}</strong>원
					</span>
				</style.TotalPrice>
				<style.Reservation>
					<style.ReservationButton
						types={reserveText[0]}
						text={reserveText[1]}
						isDisabled={reserveText[0] === "disabled"}
						waiting={reserveText[1] === "대기예약"}
						onClick={async () => {
							taggingClickReserve();
							await handleReserve();
						}}
					/>
				</style.Reservation>
				{bannerContents &&
					bannerContents.map((item, index) => (
						<style.Banner
							href={`${publicRuntimeConfig.ENV_PACKAGE_URL}${item.link}`}
							key={index}
							target={getTarget(item.target)}
							onClick={e => {
								taggingClickBanner({...item, selectDiv: "reserveBanner"});
								onClickSSO(e, `${publicRuntimeConfig.ENV_PACKAGE_URL}${item.link}`);
							}}
						>
							<img
								src={item.imgPath ? `${publicRuntimeConfig.ENV_CIMG_CDN_URL}${item.imgPath}` : errorImg}
								alt={item.alt}
							/>
						</style.Banner>
					))}
			</style.TotalWrap>
			<style.ManagerBox>
				{event.mgmUserInfo === "Y" ? (
					<dl>
						<dt>{event.mgmDeptNm}</dt>
						<dd>{event.phoneDirect}</dd>
					</dl>
				) : (
					<dl>
						<dt>담당자 {event.mgmUserNm}</dt>
						<dd>{event.mgmUserTelNo ?? event.phoneDirect}</dd>
					</dl>
				)}
			</style.ManagerBox>
		</style.Box>
	);
};

const DetailSNB = (props: Props) => (
	<ErrorBoundary logMsg="DetailSnb Error">
		<DetailSNBComponent {...props} />
	</ErrorBoundary>
);

export default memo(DetailSNB);
